  import React from 'react'
  import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
  import { BarChart3, Building2, FileText, HelpCircle, Users, ClipboardList, LogOut, Menu, ShieldCheck, X, KeyRound } from 'lucide-react'
  import toast from 'react-hot-toast'
  import { lumi } from '../lib/lumi'
  import { DEFAULT_LOCAL_PASSWORD, clearSession, createLocalCredentials, readSession, readRememberedAccess, verifyLocalPassword } from '../lib/appSession'
  import type { UserProfile } from '../lib/appSession'

  interface LayoutProps {
    userType: UserProfile
  }

  const Layout: React.FC<LayoutProps> = ({ userType }) => {
    const location = useLocation()
    const navigate = useNavigate()
    const [sidebarOpen, setSidebarOpen] = React.useState(false)
    const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false)
    const [showPasswordModal, setShowPasswordModal] = React.useState(false)
    const [currentPassword, setCurrentPassword] = React.useState('')
    const [newPassword, setNewPassword] = React.useState('')
    const [confirmPassword, setConfirmPassword] = React.useState('')
    const [savingPassword, setSavingPassword] = React.useState(false)
    const session = readSession()
    const rememberedAccess = readRememberedAccess()

    const adminNavItems = [
      { path: '/admin', label: 'Dashboard', icon: BarChart3 },
      { path: '/admin/surveys', label: 'Pesquisas', icon: FileText },
      { path: '/admin/questions', label: 'Banco de Perguntas', icon: HelpCircle },
      { path: '/admin/clients', label: 'Clientes', icon: Building2 },
      { path: '/admin/researchers', label: 'Pesquisadores', icon: Users },
      { path: '/admin/admins', label: 'Administradores', icon: ShieldCheck },
      { path: '/admin/analytics', label: 'Analises', icon: BarChart3 },
    ]

    const researcherNavItems = [
      { path: '/researcher', label: 'Dashboard', icon: ClipboardList },
      { path: '/researcher/history', label: 'Historico', icon: FileText },
    ]

    const navItems = userType === 'admin' ? adminNavItems : researcherNavItems

    const getPasswordRecord = async (profile: UserProfile, userId: string) => {
      if (profile === 'admin') {
        return await lumi.entities.admins.get(userId) as {
          passwordHash?: string
          passwordSalt?: string
        }
      }

      return await lumi.entities.researchers.get(userId) as {
        passwordHash?: string
        passwordSalt?: string
      }
    }

    const savePasswordCredentials = async (
      profile: UserProfile,
      userId: string,
      credentials: Awaited<ReturnType<typeof createLocalCredentials>>
    ) => {
      const payload = {
        ...credentials,
        updatedAt: new Date().toISOString(),
      }

      if (profile === 'admin') {
        await lumi.entities.admins.update(userId, payload)
        return
      }

      await lumi.entities.researchers.update(userId, payload)
    }

    const handleLogout = () => {
      try {
        clearSession()
        toast.success('Sessao encerrada com sucesso!')
      } catch (error) {
        console.error('Erro ao encerrar sessao:', error)
        toast.error('Erro ao encerrar sessao')
      } finally {
        navigate('/login', { replace: true })
      }
    }

    const handleChangePassword = async (event: React.FormEvent) => {
      event.preventDefault()

      const userId = session?.profile === 'admin' ? session.userId : session?.researcherId

      if (!session || !userId || userId === 'local-admin') {
        toast.error('Alteracao de senha indisponivel para este acesso.')
        return
      }

      if (newPassword.length < 6) {
        toast.error('A nova senha deve ter pelo menos 6 caracteres.')
        return
      }

      if (newPassword === DEFAULT_LOCAL_PASSWORD) {
        toast.error('Escolha uma senha diferente da senha padrao.')
        return
      }

      if (newPassword !== confirmPassword) {
        toast.error('A confirmacao da senha nao confere.')
        return
      }

      try {
        setSavingPassword(true)
        const currentUser = await getPasswordRecord(session.profile, userId)

        const currentPasswordMatches = await verifyLocalPassword(currentPassword, currentUser)

        if (!currentPasswordMatches) {
          toast.error('Senha atual invalida.')
          return
        }

        const credentials = await createLocalCredentials(newPassword, {
          mustResetPassword: false,
        })

        await savePasswordCredentials(session.profile, userId, credentials)

        setShowPasswordModal(false)
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
        toast.success('Senha alterada com sucesso.')
      } catch (error) {
        console.error('Erro ao alterar senha:', error)
        toast.error('Erro ao alterar senha')
      } finally {
        setSavingPassword(false)
      }
    }

    const handleForgetDevice = () => {
      const confirmed = window.confirm('Deseja apagar o acesso lembrado deste dispositivo?')
      if (!confirmed) return

      clearSession({ forgetRememberedAccess: true })
      toast.success('Dados locais removidos deste dispositivo.')
      navigate('/login', { replace: true })
    }

    const isActive = (path: string) => {
      if (path === '/admin' || path === '/researcher') {
        return location.pathname === path
      }

      return location.pathname.startsWith(path)
    }

    return (
      <div className="flex h-screen bg-gray-100">
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <div
          className={`
            fixed inset-y-0 left-0 z-50 w-64 shrink-0 bg-white shadow-lg transition-transform duration-300 ease-in-out lg:static
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            ${sidebarCollapsed ? 'lg:hidden' : 'lg:translate-x-0'}
          `}
        >
          <div className="flex items-center justify-between h-16 px-6 border-b">
            <h1 className="text-xl font-bold text-gray-800">
              {userType === 'admin' ? 'Administrador' : 'Pesquisador'}
            </h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-md hover:bg-gray-100"
            >
              <X size={20} />
            </button>
          </div>

          {session && (
            <div className="border-b bg-gray-50 px-6 py-4">
              <img
                src="https://static.lumi.new/material/e4/e4b79c90058e10d6a10b9828a69f2957.jpeg"
                alt="Logo DNA Survey"
                className="mx-auto mb-3 h-16 w-auto object-contain"
              />
              <p className="text-sm font-semibold text-gray-900">{session.userName}</p>
              <p className="text-xs text-gray-500 break-all">{session.email}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-[11px] font-medium text-blue-700">
                  <ShieldCheck size={12} />
                  {session.profile === 'admin' ? 'Perfil administrador' : 'Perfil pesquisador'}
                </span>
                {rememberedAccess?.email && (
                  <span className="rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-medium text-emerald-700">
                    Dispositivo lembrado
                  </span>
                )}
              </div>
            </div>
          )}

          <nav className="mt-6">
            <div className="px-3">
              {navItems.map((item) => {
                const Icon = item.icon

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      flex items-center px-3 py-2 mb-2 text-sm font-medium rounded-lg transition-colors
                      ${isActive(item.path)
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}
                    `}
                  >
                    <Icon size={20} className="mr-3" />
                    {item.label}
                  </Link>
                )
              })}
            </div>
          </nav>

          <div className="absolute bottom-0 w-full p-3">
            {(userType === 'researcher' || session?.userId !== 'local-admin') && (
              <button
                onClick={() => setShowPasswordModal(true)}
                className="mb-2 flex items-center w-full px-3 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <KeyRound size={20} className="mr-3" />
                Alterar senha
              </button>
            )}
            {rememberedAccess?.email && (
              <button
                onClick={handleForgetDevice}
                className="mb-2 flex items-center w-full px-3 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ShieldCheck size={20} className="mr-3" />
                Esquecer dispositivo
              </button>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-3 py-2 text-sm font-medium text-red-600 rounded-lg hover:bg-red-50 transition-colors"
            >
              <LogOut size={20} className="mr-3" />
              Sair
            </button>
          </div>
        </div>

        <div className="flex-1 flex flex-col overflow-hidden">
          <header className="border-b bg-white shadow-sm">
            <div className="relative flex h-16 items-center px-4 sm:px-6">
              <button
                onClick={() => {
                  if (window.matchMedia('(min-width: 1024px)').matches) {
                    setSidebarCollapsed((current) => !current)
                    return
                  }
                  setSidebarOpen(true)
                }}
                className="rounded-md p-2 text-gray-600 hover:bg-gray-100"
                aria-label={sidebarCollapsed ? 'Mostrar menu' : 'Recolher menu'}
                aria-expanded={!sidebarCollapsed}
              >
                <Menu size={20} />
              </button>
            </div>
          </header>

          <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50">
            <div className="container mx-auto px-6 py-8">
              <Outlet />
            </div>
          </main>
        </div>

        {showPasswordModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
              <div className="mb-5 flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                  <KeyRound size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Alterar senha</h2>
                  <p className="mt-1 text-sm text-gray-500">Atualize a senha do seu acesso local.</p>
                </div>
              </div>

              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Senha atual</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    autoComplete="current-password"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Nova senha</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    autoComplete="new-password"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Confirmar nova senha</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    autoComplete="new-password"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowPasswordModal(false)}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={savingPassword}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {savingPassword ? 'Salvando...' : 'Salvar senha'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    )
  }

  export default Layout